/**
 * Composable para verificar visibilidade de dados do veículo
 * Composable to check vehicle data visibility
 */

/**
 * Verifica se os dados do veículo devem ser ocultados
 * Check if vehicle data should be hidden
 *
 * @param {Object} user - Usuário logado / Logged user
 * @param {Object} vehicleRow - Dados da linha do veículo / Vehicle row data
 * @param {Date} currentTime - Tempo atual para comparação / Current time for comparison
 * @returns {boolean} - True se deve ocultar / True if should hide
 */
export function shouldHideVehicleData(user, vehicleRow, currentTime = null) {
    try {
        // Validações defensivas para carregamento inicial
        if (!user || typeof user.access_nivel === 'undefined') {
            console.warn('Usuário não disponível para verificação de visibilidade');
            return true;
        }

        if (!vehicleRow || !vehicleRow.vehicles) {
            return true;
        }

        const userAccessLevel = user.access_nivel;

        // Usuários com nível 3+ sempre veem tudo
        if (userAccessLevel >= 3) {
            return false;
        }

        // Verifica centro de custos
        const hasCostCenter = vehicleRow.vehicles.cost_center_id ||
            (vehicleRow.vehicles.costCenter && vehicleRow.vehicles.costCenter.id);

        if (hasCostCenter && user.cost_center_ids && Array.isArray(user.cost_center_ids)) {
            const vehicleCostCenterId = parseInt(vehicleRow.vehicles.cost_center_id ||
                (vehicleRow.vehicles.costCenter && vehicleRow.vehicles.costCenter.id));

            if (vehicleCostCenterId && user.cost_center_ids.includes(vehicleCostCenterId)) {
                return false;
            }
        }

        // Usa retreat_date ou RetreatDatetime como fallback (sem mutação)
        const retreatDate = vehicleRow.retreat_date || vehicleRow.RetreatDatetime;

        if (!retreatDate) {
            return true;
        }

        // Se não é a reserva do usuário, oculta
        if (vehicleRow.user_id && vehicleRow.user_id !== user.id) {
            return true;
        }

        // Verifica se ainda faltam mais de 1 hora para a retirada
        const now = currentTime || new Date();
        const retreatDateTime = new Date(retreatDate);
        const timeDifference = retreatDateTime.getTime() - now.getTime();
        const hoursDifference = timeDifference / (1000 * 60 * 60);

        // Oculta se ainda falta mais de 1 hora
        return hoursDifference > 1;

    } catch (error) {
        console.error('Erro ao verificar visibilidade do veículo:', error);
        return true; // Em caso de erro, oculta por segurança
    }
}

/**
 * Cria um template function para exibição de dados do veículo
 * Create a template function for vehicle data display
 *
 * @param {Object} user - Usuário logado / Logged user
 * @param {string} field - Campo a ser exibido ('description' ou 'board') / Field to display
 * @param {string} hiddenText - Texto a exibir quando oculto / Text to show when hidden
 * @param {string} hiddenTitle - Tooltip quando oculto / Tooltip when hidden
 * @param {Date} currentTime - Tempo atual para comparação / Current time for comparison
 * @returns {Function} - Função template / Template function
 */
export function createVehicleTemplate(user, field, hiddenText, hiddenTitle, currentTime = null) {
    return function (h, row, index) {
        if (row.vehicle_id && row.vehicles) {
            try {
                // Se o usuário não está disponível, mostra o dado por padrão
                if (!user || typeof user.access_nivel === 'undefined') {
                    return row.vehicles[field] || "Carregando...";
                }

                // NÃO muta os dados - apenas verifica visibilidade
                const shouldHide = shouldHideVehicleData(user, row, currentTime);

                if (shouldHide) {
                    return h('span', {
                        attrs: {
                            class: 'text-muted',
                            title: hiddenTitle
                        }
                    }, hiddenText);
                }

                return row.vehicles[field];

            } catch (error) {
                console.error(`Erro ao processar template ${field}:`, error);
                // Em caso de erro, retorna o valor original
                return row.vehicles[field] || "Erro";
            }
        }
        return "Sem Nome";
    };
}



// WEBPACK FOOTER //
// ./src/composables/useVehicleVisibility.js